import React, { useCallback, useState, DragEvent } from 'react';
import { cn } from '../../utils/cn';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { FolderNode as FolderNodeType, getAssetIcon } from '../../../core/types/asset.types';

interface FolderNodeProps {
  folder: FolderNodeType;
  level: number;
  selectedId: string;
  onSelect: (id: string) => void;
  dropTargetId: string | null;
  externalDropTargetId?: string | null;
}

export const FolderNode: React.FC<FolderNodeProps> = ({
  folder,
  level,
  selectedId,
  onSelect,
  dropTargetId,
  externalDropTargetId,
}) => {
  const {
    folders,
    assets,
    toggleFolderExpanded,
    setDropTarget,
    setExternalDropTarget,
    moveAsset,
    importExternalFiles,
    draggedAssetId,
    showContextMenu,
    selectAsset,
    selectedAssetId,
  } = useProjectPanelStore();
  const [isDragOver, setIsDragOver] = useState(false);
  const [isExternalDragOver, setIsExternalDragOver] = useState(false);

  const isSelected = selectedId === folder.id;
  const isDropTarget = dropTargetId === folder.id;
  const isExternalDropTarget = externalDropTargetId === folder.id;
  const hasChildren = folder.children.some((id) => folders[id] || assets[id]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(folder.id);
  }, [folder.id, onSelect]);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFolderExpanded(folder.id);
  }, [folder.id, toggleFolderExpanded]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFolderExpanded(folder.id);
  }, [folder.id, toggleFolderExpanded]);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Handle external file drops
    if (e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
      setIsExternalDragOver(true);
      setExternalDropTarget(folder.id);
    } else if (draggedAssetId) {
      // Handle internal asset dragging
      setIsDragOver(true);
      setDropTarget(folder.id);
    }
  }, [draggedAssetId, folder.id, setDropTarget, setExternalDropTarget]);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsDragOver(false);
    setIsExternalDragOver(false);
    setDropTarget(null);
    // Don't clear external drop target here - let parent handle it
  }, [setDropTarget]);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setIsExternalDragOver(false);
    setDropTarget(null);
    setExternalDropTarget(null);

    // Handle external file drops
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      importExternalFiles(files, folder.id);
    } else if (draggedAssetId) {
      // Handle internal asset move
      moveAsset(draggedAssetId, folder.id);
    }
  }, [draggedAssetId, folder.id, moveAsset, importExternalFiles, setDropTarget, setExternalDropTarget]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(folder.id);
    showContextMenu(e.clientX, e.clientY, folder.id);
  }, [folder.id, onSelect, showContextMenu]);

  const childFolders = folder.children.filter((id) => folders[id]).map((id) => folders[id]);
  const childAssets = folder.children.filter((id) => assets[id]).map((id) => assets[id]);

  const isHighlighted = isDragOver || isDropTarget || isExternalDragOver || isExternalDropTarget;

  return (
    <div>
      <div
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'flex items-center gap-1 px-2 py-1 cursor-pointer text-xs hover:bg-zinc-700/50 transition-colors',
          isSelected && 'bg-blue-600/50 text-white',
          isHighlighted && 'bg-blue-600/30 outline outline-2 outline-blue-500 outline-dashed'
        )}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
      >
        {hasChildren ? (
          <button onClick={handleToggle} className="w-4 h-4 flex items-center justify-center text-zinc-500 hover:text-zinc-300">
            {folder.expanded ? '▼' : '▶'}
          </button>
        ) : <span className="w-4" />}
        <span className="text-sm">{folder.expanded ? '📂' : '📁'}</span>
        <span className={cn('truncate flex-1', isSelected ? 'text-white' : 'text-zinc-300')}>{folder.name}</span>
      </div>
      {folder.expanded && (
        <>
          {childFolders.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              level={level + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              dropTargetId={dropTargetId}
              externalDropTargetId={externalDropTargetId}
            />
          ))}
          {childAssets.map((asset) => (
            <div
              key={asset.id}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(folder.id);
                selectAsset(asset.id);
              }}
              className={cn(
                'flex items-center gap-1 px-2 py-0.5 cursor-pointer text-xs hover:bg-zinc-700/50 transition-colors',
                selectedAssetId === asset.id && 'bg-blue-600/30 text-white'
              )}
              style={{ paddingLeft: `${(level + 1) * 12 + 8}px` }}
            >
              <span className="w-4" />
              <span className="text-sm">{getAssetIcon(asset.type)}</span>
              <span className={cn('truncate flex-1', selectedAssetId === asset.id ? 'text-white' : 'text-zinc-400')}>
                {asset.name}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
};
